import { Text as MantineText } from "@mantine/core";
import { useDebouncedState, useDebouncedValue, useHover } from "@mantine/hooks";
import { polymorphicComponentWrapper } from "../../utilities";
import styles from "./text.module.scss";
import type { CustomTextFactory, IProps, ISkeletonProps, ITextCopyProps } from "./text.types";
import { CopyButton } from "../copy-button";
import { Tooltip } from "../tooltip";
import classNames from "classnames";
import { Icon } from "../icon";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";
import { Group } from "../group";
import { Skeleton } from "../skeleton";

/**
 * Text component for displaying text content with optional styling.
 * @param {boolean} props.dimmed - Whether the text should appear dimmed.
 * @param {boolean} props.noWrap - Whether the text should not wrap to a new line.
 * @returns {JSX.Element} The Text component.
 */
const Text = polymorphicComponentWrapper<CustomTextFactory, IProps>("Text", ({ dimmed, noWrap, ...props }, ref) => {
    return (
        <MantineText
            data-dimmed={dimmed}
            data-no-wrap={noWrap}
            ref={ref}
            classNames={{ root: styles["root"] }}
            {...props}
        />
    );
});

/**
 * Skeleton loader component for Text.
 * @returns {JSX.Element} The TextSkeleton component.
 */
const TextSkeleton = (props: ISkeletonProps) => {
    return <Skeleton className={styles["skeleton"]} {...props} />;
};

/**
 * Text component with copy functionality that allows users to copy the text value.
 * @param {string} props.value - The text value to be copied.
 * @param {number} props.timeout - Timeout in milliseconds for showing the copy tooltip.
 * @param {boolean} props.disableCopy - Whether to disable the copy functionality.
 * @param {string} props.className - Additional CSS class names.
 * @returns {JSX.Element} The TextCopy component.
 */
const TextCopy = ({ value, timeout, disableCopy, className, ...textProps }: ITextCopyProps) => {
    const { hovered, ref } = useHover<HTMLParagraphElement>();
    const [debouncedOpen] = useDebouncedValue(hovered, timeout ?? 900);
    const [debouncedCopied, setDebouncedCopied] = useDebouncedState(false, 200);

    return disableCopy ? (
        <Text {...textProps} />
    ) : (
        <CopyButton value={value} timeout={timeout}>
            {({ copied, copy }) => {
                setDebouncedCopied(copied);
                return (
                    <Group justify="space-between" flex={1}>
                        <Text
                            ref={ref}
                            onClick={copy}
                            td={hovered ? "dotted" : undefined}
                            c={hovered ? "blue" : undefined}
                            className={classNames(styles["copy"], className)}
                            {...textProps}
                        />
                        {(hovered || copied) && (
                            <Tooltip
                                opened={(hovered && debouncedOpen) || copied}
                                label={copied || debouncedCopied ? "Copied" : "Copy"}
                            >
                                <Icon
                                    size="xxs"
                                    color={copied || debouncedCopied ? "teal" : "gray"}
                                    icon={copied || debouncedCopied ? faCheck : faCopy}
                                />
                            </Tooltip>
                        )}
                    </Group>
                );
            }}
        </CopyButton>
    );
};

Text.Skeleton = TextSkeleton;
Text.Copy = TextCopy;

export { Text, TextSkeleton, TextCopy };
